import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { getMessaging, getToken, onMessage, Messaging,  } from 'firebase/messaging';
import { initializeApp, getApps } from 'firebase/app';
import { UserService } from '../users/users.service';
import { User } from '@org/interfaces';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
   
  private apiUrl = `${environment.apiUrl}/api/push`;
  private messaging: Messaging;
  private usr!: User;

  constructor(private http: HttpClient, private user: UserService, private auth: AuthService) {
    const app = getApps()[0] ?? initializeApp(environment.firebaseConfig);
    this.messaging = getMessaging(app);
  }

  requestPermission(): Observable<void> {
    return new Observable<void>((observer) => {
      // Check the current Notification permission status
      if (Notification.permission === 'granted') {
        // Permission already granted
        this.getTokenAndSendToServer(observer);
      } else if (Notification.permission === 'default') {
        // Permission has not been asked yet or was dismissed
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            // Permission granted by the user
            this.getTokenAndSendToServer(observer);
          } else {
            // Permission denied or dismissed
            console.warn('Notification permission not granted.');
            observer.complete();
          }
        }).catch(err => {
          console.error('Error requesting notification permission:', err);
          observer.error(err);
        });
      } else if (Notification.permission === 'denied') {
        // Permission was previously denied
        console.warn('Notification permission previously denied.');
        observer.complete();
      } else {
        // Handle any unexpected states
        console.warn('Unexpected Notification.permission state:', Notification.permission);
        observer.complete();
      }
    });
  }
  
  
  private getTokenAndSendToServer(observer: any): void {
    getToken(this.messaging, {
      vapidKey: 'BJHDhSVKgIJM50ca8PHzecCChcbppML-n7XsEUlq2plwfwETC7V5UulX4r6CsX34BjIQN-ekj9By4Dfa1VZRHyM'
    }).then((currentToken) => {
      if (currentToken) {
       
        this.sendTokenToServer(currentToken).subscribe({
          next: () => {
            observer.next();
            observer.complete();  // Complete the observable when done
          },
          error: (err) => {
            observer.error(err);  // Pass the error to the observer
          }
        });
      } else {
       
        observer.complete();  // Complete even if no token is available
      }
    }).catch((err) => {
      console.error('An error occurred while retrieving token.', err);
      observer.error(err);  // Pass the error to the observer
    });
  }

  private sendTokenToServer(token: string): Observable<void> {
    const userId = this.getUserValue()?._id;
    const tenantId = this.getUserValue()?.tenant;
    if (!userId) {
      console.error('User ID not found. Token will not be sent to the server.');
      return throwError(() => new Error('User ID not found'));
    }
  
    return this.http.post<any>(`${this.apiUrl}/subscribe`, { userId, token, tenantId }).pipe(
      catchError(error => {
        console.error('Failed to send token to server', error);
        return throwError(() => new Error(error.message));
      })
    );
  }
  
  // Listen for incoming messages when the app is in the foreground
  listenForMessages() {
    onMessage(this.messaging, (payload) => {
      
      this.displayNotification(payload);
    });
  }

 

  private displayNotification(payload: any) {
    if (Notification.permission === 'granted') {
      const title = payload.notification?.title || 'New Message';
      const options = {
        body: payload.notification?.body || '',
        icon: payload.notification?.icon || '/icons/icon-192x192.png',
        data: payload.data || {} // Pass additional data (like URL) to the notification
      };
  
      const notification = new Notification(title, options);
  
      notification.onclick = (event) => {
        event.preventDefault(); // Prevent the browser from focusing the Notification's tab
        if (options.data.url) {
          window.open(options.data.url, '_blank'); // Open the target URL in a new tab
        }
      };
    }
  }
  

  // Placeholder method to get the current user
  getUserValue(): any {
    return this.user.getUserValue();
  }

  private subscribeToNotifications(userId: string, token: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/subscribe`, { userId, token });
  }

   // Method to send a notification to a specific user
   sendNotificationToUser(userId: string, messagePayload: { title: string; body: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/sendNotificationToUser`, { userId, messagePayload });
  }

  // Method to send a notification to all users
  sendNotificationToAllUsers(messagePayload: { title: string; body: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/sendNotificationToAllUsers`, { messagePayload });
  }


  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  notifyUser(userId: string, title: string = 'New Task Assigned', body: string = 'You have been assigned a new task.'): Observable<any> {
    const messagePayload = {
      title: title,
      body: body,
    };
    return this.sendNotificationToUser(userId, messagePayload);
  }

  notifyAllUsers(title: string, body: string) {
    const messagePayload = {
      title: title,
      body: body,
    };
    return this.sendNotificationToAllUsers(messagePayload);
  }
    
  


}
